import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { workdStorage } from '../storage/workdStorage';

/**
 * Cached announcements data structure
 */
export interface CachedAnnouncements {
	data: Array<unknown>;
	timestamp: number;
	location: string;
}

/**
 * Announcements store state
 */
export interface AnnouncementsStoreState {
	dismissedIds: Array<number>;
	cachedAnnouncements: CachedAnnouncements | null;
}

/**
 * Announcements store actions
 */
export interface AnnouncementsStoreActions {
	dismissAnnouncement: (id: number) => void;
	isDismissed: (id: number) => boolean;
	setCachedAnnouncements: (data: Array<unknown>, location: string) => void;
	getCachedAnnouncements: (
		location: string,
		ttl: number,
	) => Array<unknown> | null;
	clearCache: () => void;
	clearAll: () => void;
}

const initialState: AnnouncementsStoreState = {
	dismissedIds: [],
	cachedAnnouncements: null,
};

/**
 * Zustand store for announcements state.
 * Persists to localStorage with workd:announcements:state key.
 *
 * Stores:
 * - Dismissed announcement IDs (persisted)
 * - Cached active announcements with TTL (session-like, but persisted)
 */
export const useAnnouncementsStore = create<
	AnnouncementsStoreState & AnnouncementsStoreActions
>()(
	persist(
		(set, get) => ({
			...initialState,

			dismissAnnouncement: (id) => {
				const currentIds = get().dismissedIds;

				if (!currentIds.includes(id)) {
					set({ dismissedIds: [...currentIds, id] });
				}
			},

			isDismissed: (id) => get().dismissedIds.includes(id),

			setCachedAnnouncements: (data, location) => {
				set({
					cachedAnnouncements: {
						data,
						timestamp: Date.now(),
						location,
					},
				});
			},

			getCachedAnnouncements: (location, ttl) => {
				const cached = get().cachedAnnouncements;

				if (cached == null) {
					return null;
				}

				if (cached.location !== location) {
					return null;
				}

				if (Date.now() - cached.timestamp >= ttl) {
					return null;
				}

				return cached.data;
			},

			clearCache: () => {
				set({ cachedAnnouncements: null });
			},

			clearAll: () => {
				set(initialState);
			},
		}),
		{
			name: 'workd:announcements:state',
			storage: workdStorage,
		},
	),
);
